import { compareInstantsDesc } from './roster-chronology';

export type FrontlineClinicalReviewDomain = 'observation' | 'medication' | 'incident' | 'wound';

export type FrontlineClinicalReviewPriority = 'critical' | 'warning';

export interface FrontlineClinicalReviewItem {
  id: string;
  domain: FrontlineClinicalReviewDomain;
  priority: FrontlineClinicalReviewPriority;
  clientId: string | null;
  title: string;
  subtitle: string;
  href: string;
  occurredAt: string | null;
}

export type FrontlineObservationReviewLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  observationType?: string | null;
  isAbnormal?: boolean | null;
  alertTriggered?: boolean | null;
  recordedAt?: string | null;
};

export type FrontlineMedicationReviewLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  medicationName?: string | null;
  status?: string | null;
  endDate?: string | null;
  isPolypharmacy?: boolean | null;
};

export type FrontlineIncidentReviewLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  incidentType?: string | null;
  severity?: string | null;
  investigationStatus?: string | null;
  reportable?: boolean | null;
  commissionNotified?: boolean | null;
  commissionDeadline?: string | null;
  occurredAt?: string | null;
};

export type FrontlineWoundReviewLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  woundType?: string | null;
  status?: string | null;
  infectionSuspected?: boolean | null;
  referredTo?: string | null;
  referralDate?: string | null;
  onsetDate?: string | null;
  updatedAt?: string | null;
  lastAssessmentAt?: string | null;
  healingTrajectory?: string | null;
};

export interface FrontlineClinicalReviewQueueSummary {
  total: number;
  criticalCount: number;
  observationCount: number;
  medicationCount: number;
  incidentCount: number;
  woundCount: number;
  woundInfectedCount: number;
  woundWorseningCount: number;
  woundReferredCount: number;
  woundOverdueAssessmentCount: number;
  items: FrontlineClinicalReviewItem[];
}

export type FrontlineAssessmentFollowThroughLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  assessmentType?: string | null;
  status?: string | null;
  nextReviewDate?: string | null;
};

export type FrontlineCarePlanFollowThroughLike = {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  planType?: string | null;
  status?: string | null;
  reviewDate?: string | null;
};

export interface FrontlineClinicalWorkflowContinuityItem {
  clientId: string;
  clientName: string;
  reviewCount: number;
  criticalCount: number;
  domains: FrontlineClinicalReviewDomain[];
  hasAssessmentFollowThrough: boolean;
  hasCarePlanFollowThrough: boolean;
  assessmentHref: string;
  carePlanHref: string;
}

export interface FrontlineClinicalWorkflowContinuitySummary {
  impactedClientCount: number;
  criticalClientCount: number;
  assessmentFollowThroughCount: number;
  assessmentGapCount: number;
  carePlanFollowThroughCount: number;
  carePlanGapCount: number;
  fullyCoveredCount: number;
  items: FrontlineClinicalWorkflowContinuityItem[];
}

type SummaryOptions = {
  now?: Date;
  woundReviewDays?: number;
  medicationExpiryDays?: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const CRITICAL_PRIORITY_WEIGHT: Record<FrontlineClinicalReviewPriority, number> = {
  critical: 0,
  warning: 1,
};

function toUpper(value: string | null | undefined): string {
  return (value || '').trim().toUpperCase();
}

function formatLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getClientName(value: string | null | undefined): string {
  return value?.trim() || 'Unknown client';
}

function sortItems(items: FrontlineClinicalReviewItem[]): FrontlineClinicalReviewItem[] {
  return [...items].sort((left, right) => {
    const priorityDelta =
      CRITICAL_PRIORITY_WEIGHT[left.priority] - CRITICAL_PRIORITY_WEIGHT[right.priority];
    if (priorityDelta !== 0) return priorityDelta;
    // Newest-first inside a priority band decides which clinical items a reviewer
    // reads at the top of the queue. `occurredAt` is coalesced from four different
    // producers (observation `recordedAt` timestamptz, medication `endDate` bare
    // DATE, incident `occurredAt`/commission deadline, wound review anchor), so a
    // text sort compared `2026-08-17` against `2026-08-17T09:00:00+00:00`. The bare
    // date is a *prefix* of the timestamped form, which pinned every date-only item
    // below every timestamped item on the same day irrespective of the real instant.
    // Compare parsed instants, and keep undated rows last rather than letting them
    // float to the head of the queue.
    // `compareInstantsDesc` is newest-first AND keeps undated rows last, which is
    // this whole rule in one call. The hand-rolled dated/undated guard it replaces
    // was correct, but it is exactly the shape someone later "simplifies" back
    // into a plain `compareInstants(right, left)` — which silently floats undated
    // rows to the HEAD of the queue.
    return compareInstantsDesc(left.occurredAt, right.occurredAt);
  });
}

function hasAssessmentFollowThrough(
  assessment: FrontlineAssessmentFollowThroughLike,
  now: Date,
  reviewWindowDays: number,
): boolean {
  const status = toUpper(assessment.status);
  if (status === 'IN_PROGRESS' || status === 'SCHEDULED' || status === 'OVERDUE') return true;
  if (!assessment.nextReviewDate) return false;
  const dueTime = new Date(assessment.nextReviewDate).getTime();
  const deltaDays = Math.ceil((dueTime - now.getTime()) / DAY_MS);
  return deltaDays <= reviewWindowDays;
}

function hasCarePlanFollowThrough(
  carePlan: FrontlineCarePlanFollowThroughLike,
  now: Date,
  reviewWindowDays: number,
): boolean {
  const status = toUpper(carePlan.status);
  if (status === 'IN_REVIEW' || status === 'PENDING_APPROVAL') return true;
  if (!carePlan.reviewDate) return false;
  const reviewTime = new Date(carePlan.reviewDate).getTime();
  const deltaDays = Math.ceil((reviewTime - now.getTime()) / DAY_MS);
  return deltaDays <= reviewWindowDays;
}

export function summariseFrontlineClinicalReviewQueue(
  input: {
    observations?: readonly FrontlineObservationReviewLike[];
    medications?: readonly FrontlineMedicationReviewLike[];
    incidents?: readonly FrontlineIncidentReviewLike[];
    wounds?: readonly FrontlineWoundReviewLike[];
  },
  options: SummaryOptions = {},
): FrontlineClinicalReviewQueueSummary {
  const now = options.now ?? new Date();
  const woundReviewDays = options.woundReviewDays ?? 7;
  const medicationExpiryDays = options.medicationExpiryDays ?? 60;
  const items: FrontlineClinicalReviewItem[] = [];

  let observationCount = 0;
  let medicationCount = 0;
  let incidentCount = 0;
  let woundCount = 0;
  let criticalCount = 0;
  let woundInfectedCount = 0;
  let woundWorseningCount = 0;
  let woundReferredCount = 0;
  let woundOverdueAssessmentCount = 0;

  for (const observation of input.observations || []) {
    const id = observation.id || null;
    if (!id) continue;

    const alertTriggered = observation.alertTriggered === true;
    const isAbnormal = observation.isAbnormal === true;
    if (!alertTriggered && !isAbnormal) continue;

    observationCount += 1;
    if (alertTriggered) criticalCount += 1;

    items.push({
      id,
      domain: 'observation',
      priority: alertTriggered ? 'critical' : 'warning',
      clientId: observation.clientId || null,
      title: getClientName(observation.clientName),
      subtitle: `${formatLabel(observation.observationType)} ${alertTriggered ? 'triggered an alert' : 'is abnormal'}`,
      href: observation.clientId ? `/clients/${observation.clientId}` : '/observations',
      occurredAt: observation.recordedAt || null,
    });
  }

  for (const medication of input.medications || []) {
    const id = medication.id || null;
    if (!id) continue;

    const status = toUpper(medication.status);
    const onHold = status === 'ON_HOLD';
    const isPolypharmacy = medication.isPolypharmacy === true;
    const endDate = medication.endDate ? new Date(medication.endDate) : null;
    const daysToExpiry = endDate ? Math.ceil((endDate.getTime() - now.getTime()) / DAY_MS) : null;
    const isExpiring =
      daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= medicationExpiryDays;

    if (!onHold && !isPolypharmacy && !isExpiring) continue;

    medicationCount += 1;
    if (onHold) criticalCount += 1;

    let subtitle = 'requires medication review';
    if (onHold) subtitle = 'is currently on hold';
    else if (isExpiring)
      subtitle = `script expires in ${daysToExpiry} day${daysToExpiry === 1 ? '' : 's'}`;
    else if (isPolypharmacy) subtitle = 'is part of a polypharmacy profile';

    items.push({
      id,
      domain: 'medication',
      priority: onHold ? 'critical' : 'warning',
      clientId: medication.clientId || null,
      title: medication.medicationName?.trim() || 'Medication review required',
      subtitle,
      href: medication.clientId ? `/clients/${medication.clientId}` : '/medications',
      occurredAt: medication.endDate || null,
    });
  }

  for (const incident of input.incidents || []) {
    const id = incident.id || null;
    if (!id) continue;

    const severity = toUpper(incident.severity);
    const investigationStatus = toUpper(incident.investigationStatus);
    const overdueNotification =
      incident.reportable === true &&
      incident.commissionNotified !== true &&
      !!incident.commissionDeadline &&
      new Date(incident.commissionDeadline).getTime() < now.getTime();
    const needsReview =
      overdueNotification ||
      severity === 'CRITICAL' ||
      severity === 'MAJOR' ||
      investigationStatus === 'OPEN' ||
      investigationStatus === 'UNDER_INVESTIGATION' ||
      investigationStatus === 'ACTION_REQUIRED';

    if (!needsReview) continue;

    incidentCount += 1;
    const priority: FrontlineClinicalReviewPriority =
      overdueNotification || severity === 'CRITICAL' ? 'critical' : 'warning';
    if (priority === 'critical') criticalCount += 1;

    let subtitle = `${formatLabel(incident.incidentType)} incident requires follow-up`;
    if (overdueNotification) subtitle = 'SIRS notification is overdue';
    else if (investigationStatus === 'UNDER_INVESTIGATION') subtitle = 'is under investigation';
    else if (investigationStatus === 'ACTION_REQUIRED') subtitle = 'has outstanding actions';

    items.push({
      id,
      domain: 'incident',
      priority,
      clientId: incident.clientId || null,
      title: getClientName(incident.clientName),
      subtitle,
      href: `/compliance/incidents/${id}`,
      occurredAt: incident.occurredAt || incident.commissionDeadline || null,
    });
  }

  for (const wound of input.wounds || []) {
    const id = wound.id || null;
    if (!id) continue;

    const status = toUpper(wound.status);
    const infected = status === 'INFECTED' || wound.infectionSuspected === true;
    const worsening = status === 'WORSENING';
    const referred = status === 'REFERRED' || !!wound.referralDate || !!wound.referredTo;
    const reviewAnchor = wound.lastAssessmentAt || wound.updatedAt || wound.onsetDate || null;
    const overdueAssessment =
      status !== 'HEALED' &&
      !!reviewAnchor &&
      now.getTime() - new Date(reviewAnchor).getTime() > woundReviewDays * DAY_MS;

    if (!infected && !worsening && !referred && !overdueAssessment) continue;

    woundCount += 1;
    if (infected) woundInfectedCount += 1;
    if (worsening) woundWorseningCount += 1;
    if (referred) woundReferredCount += 1;
    if (overdueAssessment) woundOverdueAssessmentCount += 1;

    const priority: FrontlineClinicalReviewPriority =
      infected || worsening ? 'critical' : 'warning';
    if (priority === 'critical') criticalCount += 1;

    let subtitle = `${formatLabel(wound.woundType)} requires review`;
    if (infected) subtitle = `${formatLabel(wound.woundType)} has infection concerns`;
    else if (worsening) subtitle = `${formatLabel(wound.woundType)} is worsening`;
    else if (referred)
      subtitle = `${formatLabel(wound.woundType)} has external review or referral in progress`;
    else if (overdueAssessment)
      subtitle = `${formatLabel(wound.woundType)} is overdue for reassessment`;

    items.push({
      id,
      domain: 'wound',
      priority,
      clientId: wound.clientId || null,
      title: getClientName(wound.clientName),
      subtitle,
      href: wound.clientId ? `/clients/${wound.clientId}` : '/wounds',
      occurredAt: reviewAnchor,
    });
  }

  return {
    total: observationCount + medicationCount + incidentCount + woundCount,
    criticalCount,
    observationCount,
    medicationCount,
    incidentCount,
    woundCount,
    woundInfectedCount,
    woundWorseningCount,
    woundReferredCount,
    woundOverdueAssessmentCount,
    items: sortItems(items),
  };
}

export function summariseFrontlineClinicalWorkflowContinuity(
  input: {
    reviewItems?: readonly FrontlineClinicalReviewItem[];
    assessments?: readonly FrontlineAssessmentFollowThroughLike[];
    carePlans?: readonly FrontlineCarePlanFollowThroughLike[];
  },
  options: { now?: Date; reviewWindowDays?: number } = {},
): FrontlineClinicalWorkflowContinuitySummary {
  const now = options.now ?? new Date();
  const reviewWindowDays = options.reviewWindowDays ?? 30;
  const reviewItems = input.reviewItems || [];
  const reviewByClient = new Map<
    string,
    {
      clientName: string;
      reviewCount: number;
      criticalCount: number;
      domains: Set<FrontlineClinicalReviewDomain>;
    }
  >();

  for (const item of reviewItems) {
    if (!item.clientId) continue;
    const current = reviewByClient.get(item.clientId) ?? {
      clientName: item.title || 'Unknown client',
      reviewCount: 0,
      criticalCount: 0,
      domains: new Set<FrontlineClinicalReviewDomain>(),
    };
    current.reviewCount += 1;
    if (item.priority === 'critical') current.criticalCount += 1;
    current.domains.add(item.domain);
    reviewByClient.set(item.clientId, current);
  }

  const assessmentsByClient = new Map<string, FrontlineAssessmentFollowThroughLike[]>();
  for (const assessment of input.assessments || []) {
    if (!assessment.clientId) continue;
    const rows = assessmentsByClient.get(assessment.clientId) ?? [];
    rows.push(assessment);
    assessmentsByClient.set(assessment.clientId, rows);
  }

  const carePlansByClient = new Map<string, FrontlineCarePlanFollowThroughLike[]>();
  for (const carePlan of input.carePlans || []) {
    if (!carePlan.clientId) continue;
    const rows = carePlansByClient.get(carePlan.clientId) ?? [];
    rows.push(carePlan);
    carePlansByClient.set(carePlan.clientId, rows);
  }

  const items: FrontlineClinicalWorkflowContinuityItem[] = [];
  let criticalClientCount = 0;
  let assessmentFollowThroughCount = 0;
  let carePlanFollowThroughCount = 0;
  let fullyCoveredCount = 0;

  for (const [clientId, review] of reviewByClient.entries()) {
    const hasAssessment = (assessmentsByClient.get(clientId) || []).some((assessment) =>
      hasAssessmentFollowThrough(assessment, now, reviewWindowDays),
    );
    const hasCarePlan = (carePlansByClient.get(clientId) || []).some((carePlan) =>
      hasCarePlanFollowThrough(carePlan, now, reviewWindowDays),
    );

    if (review.criticalCount > 0) criticalClientCount += 1;
    if (hasAssessment) assessmentFollowThroughCount += 1;
    if (hasCarePlan) carePlanFollowThroughCount += 1;
    if (hasAssessment && hasCarePlan) fullyCoveredCount += 1;

    items.push({
      clientId,
      clientName: review.clientName,
      reviewCount: review.reviewCount,
      criticalCount: review.criticalCount,
      domains: [...review.domains].sort(),
      hasAssessmentFollowThrough: hasAssessment,
      hasCarePlanFollowThrough: hasCarePlan,
      assessmentHref: `/clients/${clientId}/assessments`,
      carePlanHref: `/clients/${clientId}?tab=care-plans`,
    });
  }

  items.sort((left, right) => {
    const leftGapScore =
      (left.hasAssessmentFollowThrough ? 0 : 1) + (left.hasCarePlanFollowThrough ? 0 : 1);
    const rightGapScore =
      (right.hasAssessmentFollowThrough ? 0 : 1) + (right.hasCarePlanFollowThrough ? 0 : 1);
    if (rightGapScore !== leftGapScore) return rightGapScore - leftGapScore;
    if (right.criticalCount !== left.criticalCount) return right.criticalCount - left.criticalCount;
    return right.reviewCount - left.reviewCount;
  });

  const impactedClientCount = items.length;

  return {
    impactedClientCount,
    criticalClientCount,
    assessmentFollowThroughCount,
    assessmentGapCount: impactedClientCount - assessmentFollowThroughCount,
    carePlanFollowThroughCount,
    carePlanGapCount: impactedClientCount - carePlanFollowThroughCount,
    fullyCoveredCount,
    items,
  };
}
